import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from './client.js';

/**
 * Live updates from the control plane.
 *
 * `fetch` with a streaming body rather than `EventSource`, because EventSource
 * cannot send an Authorization header -- which is why WebSocket and EventSource
 * auth so often ends up as a token in a query string. A plain fetch carries the
 * bearer token the same way every other request does.
 *
 * Events are NUDGES. Nothing here carries a registration; it says "something
 * changed for this tenant" and the caller refetches through the normal API. One
 * code path reads data, one set of permissions governs it.
 *
 * The chain end to end: FreeSWITCH raises sofia::register, the agent pushes
 * within about a second, the API fans out, the browser refetches. Roughly two
 * seconds from handset to screen, against the thirty-second sweep it replaces.
 */

export type RealtimeState = 'connecting' | 'live' | 'offline';

const RETRY_MIN_MS = 1_000;
const RETRY_MAX_MS = 30_000;

/**
 * `platform` watches EVERY tenant on one connection.
 *
 * The alternative -- a stream per tenant -- means an operator with thirty
 * accounts holds thirty connections and has to reopen the set whenever one is
 * created. The API refuses the platform channel to anyone whose scope is not
 * actually the whole platform.
 */
export interface ExtensionLive {
  user: string; domain: string;
  state: 'idle' | 'ringing' | 'on_call';
  since: number;
  direction: 'inbound' | 'outbound' | 'internal' | null;
  peer: string | null;
}
export interface QueueLive {
  queue: string; name: string; domain: string; waiting: number;
  agents_total: number; agents_available: number; agents_on_call: number;
  calls_answered: number; longest_wait_sec: number;
}

export function useRealtime(
  tenantId: string | null,
  opts: {
    platform?: boolean;
    /**
     * Call state arrives as a PAYLOAD, not a nudge.
     *
     * Registration changes are rare enough that refetching is simplest, but a
     * bridged call fires several transitions in a second and refetching each
     * would turn one call into six requests. This state also has no
     * permissions of its own beyond the tenant it was published to.
     */
    onTelephony?: (data: { extensions: ExtensionLive[]; queues: QueueLive[] }) => void;
  } = {},
): RealtimeState {
  const qc = useQueryClient();
  const [state, setState] = useState<RealtimeState>('connecting');
  const platform = opts.platform === true;
  // Held in a ref so a new callback identity does not tear down the
  // connection: the stream should outlive a parent re-render.
  const onTelephonyRef = useRef(opts.onTelephony);
  onTelephonyRef.current = opts.onTelephony;

  useEffect(() => {
    if (!tenantId && !platform) return;
    let stopped = false;
    let controller: AbortController | null = null;
    let retry = RETRY_MIN_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const connect = async (): Promise<void> => {
      if (stopped) return;
      controller = new AbortController();
      try {
        const url = platform
          ? '/api/v1/realtime'
          : `/api/v1/realtime?tenant_id=${encodeURIComponent(tenantId!)}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${getAccessToken() ?? ''}`, Accept: 'text/event-stream' },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`realtime ${res.status}`);

        setState('live');
        retry = RETRY_MIN_MS;

        const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done || stopped) break;
          buffer += value;

          // SSE frames are separated by a blank line. Anything short of one is
          // a partial frame and must stay in the buffer.
          let split: number;
          while ((split = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);
            if (frame.startsWith(':')) continue;   // keepalive comment

            const event = /^event:\s*(.+)$/m.exec(frame)?.[1]?.trim();
            if (event === 'registrations') {
              // Invalidate rather than patch: the extension row carries the
              // registration alongside 40 other fields, and half-updating it
              // from an event payload is how two sources of truth start.
              if (platform) {
                void qc.invalidateQueries({ queryKey: ['live-extensions'] });
              } else {
                void qc.invalidateQueries({ queryKey: ['extension', tenantId] });
                void qc.invalidateQueries({ queryKey: ['extensions', tenantId] });
              }
            } else if (event === 'telephony' && onTelephonyRef.current) {
              const data = /^data:\s*(.+)$/m.exec(frame)?.[1];
              if (data) {
                try { onTelephonyRef.current(JSON.parse(data)); }
                catch { /* a frame we cannot parse is one frame, not a failure */ }
              }
            }
          }
        }
      } catch {
        // Aborts, network drops and 5xx all land here and are all the same
        // thing: reconnect. A failed stream must never surface as an error --
        // the page is still correct, just no longer live.
      }
      if (stopped) return;
      setState('offline');
      timer = setTimeout(() => void connect(), retry);
      retry = Math.min(retry * 2, RETRY_MAX_MS);
    };

    void connect();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      controller?.abort();
    };
  }, [tenantId, platform, qc]);

  return state;
}
